import { Link } from 'react-router-dom';
import styled from 'styled-components';
import type { GroupRatePick, GroupRateSuggestion, GroupRateSuggestionModel } from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { SkeletonLine } from '@shared/ui/Skeleton';
import {
  Notice,
  PICK_LABEL,
  TextButton,
  basisText,
  fleetLeading,
  fleetType,
  formatDecimal,
  formatIsoDate,
  formatMoney,
  rateUnit,
  typo,
} from '@entities/fleet';

const Panel = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};

  &[aria-busy='true'] .values {
    opacity: 0.55;
  }

  @container drawer (max-width: 520px) {
    padding: ${({ theme }) => theme.spacing(3)};
  }
`;

const Fieldset = styled.fieldset`
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;

  & > legend {
    margin-bottom: ${({ theme }) => theme.spacing(2)};
    padding: 0;
    font-size: ${fleetType.body};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};
  transition: opacity ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  @container drawer (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/** Среднее — карточка-вариант: крупная цифра, как её посчитали; выбранная — кромкой. */
const Card = styled.label`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2.5)}`};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3.5)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }

  &:has(input:checked) {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.primary};
  }

  &:has(input:focus-visible) {
    box-shadow:
      inset 0 0 0 1px ${({ theme }) => theme.colors.primary},
      ${({ theme }) => theme.shadow.focus};
  }

  & > input {
    width: 16px;
    height: 16px;
    margin: 3px 0 0;
    accent-color: ${({ theme }) => theme.colors.primary};
  }

  & > .label {
    font-size: ${fleetType.body};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & > .value,
  & > .hint {
    grid-column: 2;
  }

  & > .value {
    font-size: ${fleetType.title};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  & > .hint {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

/** Те же цифры для справки, когда ставку вводят вручную. */
const Reference = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};
  margin: 0;

  & dt {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & dd {
    margin: ${({ theme }) => `${theme.spacing(0.5)} 0 0`};
    font-size: ${fleetType.lead};
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

const Line = styled.p`
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;

  & > strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
    font-variant-numeric: tabular-nums;
  }
`;

const ModelsHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};

  & > p {
    margin: 0;
    font-size: ${fleetType.small};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & > button {
    font-size: ${fleetType.small};
  }
`;

const Models = styled.ul`
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  padding: 0;
  list-style: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

/** Модель — строка-флажок: название и версия ПРЦ слева, значение справа. */
const ModelRow = styled.li`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  & > label {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: baseline;
    gap: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2.5)}`};
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(1)}`};
    cursor: pointer;
  }

  & > label:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }

  & input {
    width: 16px;
    height: 16px;
    margin: 0;
    align-self: center;
    accent-color: ${({ theme }) => theme.colors.primary};
  }

  & .name {
    font-size: ${fleetType.body};
    color: ${({ theme }) => theme.colors.text.primary};
    overflow-wrap: anywhere;
  }

  & .value {
    font-size: ${fleetType.body};
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  & .meta {
    grid-column: 2 / -1;
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    overflow-wrap: anywhere;
  }

  &[data-off='true'] .name,
  &[data-off='true'] .value {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: 400;
    text-decoration: line-through;
  }
`;

const Missing = styled.div`
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};

  & > p {
    margin: 0 0 ${({ theme }) => theme.spacing(1)};
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }

  & > ul {
    margin: 0;
    padding-left: ${({ theme }) => theme.spacing(4)};
  }
`;

const Empty = styled.div`
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.primary};

  & > p {
    margin: 0;
    text-wrap: pretty;
  }

  & > p + p {
    margin-top: ${({ theme }) => theme.spacing(2)};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & a {
    font-weight: 600;
  }
`;

const PICKS: readonly GroupRatePick[] = ['average', 'weighted'];

function count(raw: string): string {
  return formatDecimal(raw, { minFraction: 0, maxFraction: 3, grouping: true }) ?? raw;
}

function modelsWord(n: number): string {
  return pluralizeRu(n, ['модели', 'моделям', 'моделям']);
}

function modelMeta(m: GroupRateSuggestionModel, year: number | null): string {
  return [
    `${m.units_count} ед.`,
    m.version_label
      ? `версия «${m.version_label}» с ${formatIsoDate(m.valid_from)}`
      : `ПРЦ с ${formatIsoDate(m.valid_from)}`,
    year !== null && m.year !== year ? `цены ${m.year} г.` : null,
  ]
    .filter((s): s is string => s !== null)
    .join(' · ');
}

interface SuggestionPanelProps {
  /** Подсказка по всему парку группы — список моделей со значениями. */
  full: GroupRateSuggestion | undefined;
  /** По отмеченным моделям; отмечены все — та же, что `full`. */
  active: GroupRateSuggestion | undefined;
  loading: boolean;
  /** Пересчитывается по отмеченным моделям. */
  updating: boolean;
  error: string | null;
  /** Ставку берут из подсказки — выбор среднего; иначе цифры для справки. */
  picking: boolean;
  pick: GroupRatePick;
  onPick: (pick: GroupRatePick) => void;
  /** Отмеченные модели; `null` — все. */
  checked: ReadonlySet<string> | null;
  onChecked: (next: Set<string> | null) => void;
  date: string;
  /** Ссылка на группу в справочнике — добавить модели. */
  groupHref: string;
  idPrefix: string;
}

/**
 * Подсказка ставки (п. 6.3.2): простое и средневзвешенное по единицам среднее
 * утверждённых ПРЦ моделей группы на дату, разброс, модели с галочками «только
 * эти для объекта» и модели без ПРЦ отдельно.
 */
export function SuggestionPanel({
  full,
  active,
  loading,
  updating,
  error,
  picking,
  pick,
  onPick,
  checked,
  onChecked,
  date,
  groupHref,
  idPrefix,
}: SuggestionPanelProps) {
  if (error) {
    return (
      <Notice $tone="danger" role="alert">
        <span className="text">{error}</span>
      </Notice>
    );
  }
  if (loading || !full) {
    return (
      <Panel aria-busy="true" aria-label="Считаю подсказку">
        <SkeletonLine $height={56} />
        <SkeletonLine $width="70%" />
        <SkeletonLine $width="90%" />
      </Panel>
    );
  }

  const dateText = formatIsoDate(date);
  if (full.models_count === 0) {
    const noModels = full.missing_prc.length === 0;
    return (
      <Panel>
        <Empty>
          <p>
            <strong>
              {noModels
                ? 'В группе нет моделей в работе — подсказку считать не из чего.'
                : `Ни у одной модели группы нет утверждённой ПРЦ на ${dateText}.`}
            </strong>
          </p>
          <p>
            {noModels ? (
              <>
                {typo('Задайте ставку вручную или добавьте модели в группу в ')}
                <Link to={groupHref}>справочнике техники</Link>.
              </>
            ) : (
              <>
                {typo('Задайте ставку вручную или сначала утвердите ПРЦ в разделе ')}
                <Link to="/agents/econ/prc">«Версии ПРЦ»</Link>.
              </>
            )}
          </p>
        </Empty>
        {!noModels && <MissingList full={full} dateText={dateText} />}
      </Panel>
    );
  }

  const all = full.models.map((m) => m.model_id);
  const isOn = (id: string) => checked === null || checked.has(id);
  const picked = all.filter(isOn).length;
  const stats = picked === 0 ? null : active;
  const unitText = rateUnit('machine_hour');
  const usedYear = full.year;
  const yearsDiffer = full.models.some((m) => usedYear !== null && m.year !== usedYear);

  function toggle(id: string) {
    const next = new Set(checked ?? all);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChecked(next.size === all.length ? null : next);
  }

  const value = (k: GroupRatePick) => (stats ? formatMoney(stats[k]) : '—');
  const hint: Readonly<Record<GroupRatePick, string>> = {
    average: stats ? `простое, по ${stats.models_count} ${modelsWord(stats.models_count)}` : '',
    weighted: stats ? `по единицам техники: ${count(stats.units_count)} ед.` : '',
  };

  return (
    <Panel aria-busy={updating}>
      {/* Сервер сам говорит, годится ли подсказка ставкой: «в сутках» — нет. */}
      {!full.applicable && (
        <Notice $tone="warning" role="note">
          <span className="text">
            {typo(
              'Группа учитывается в сутках на объекте, а ПРЦ — цена машино-часа. В ставку за сутки подсказку система не переводит — введите ставку вручную. Цифры ниже — для справки, в рублях за маш.-час.',
            )}
          </span>
        </Notice>
      )}

      <div>
        {picking ? (
          <Fieldset>
            <legend>Какое среднее принять</legend>
            <Cards className="values">
              {PICKS.map((k) => (
                <Card key={k}>
                  <input
                    type="radio"
                    name={`${idPrefix}-pick`}
                    value={k}
                    checked={pick === k}
                    disabled={!stats}
                    onChange={() => onPick(k)}
                  />
                  <span className="label">{PICK_LABEL[k]}</span>
                  <span className="value">{value(k)}</span>
                  <span className="hint">{hint[k]}</span>
                </Card>
              ))}
            </Cards>
          </Fieldset>
        ) : (
          <Reference className="values" aria-label="Подсказка системы для справки">
            <div>
              <dt>Среднее · {hint.average}</dt>
              <dd>{value('average')}</dd>
            </div>
            <div>
              <dt>Средневзвешенное · {hint.weighted}</dt>
              <dd>{value('weighted')}</dd>
            </div>
          </Reference>
        )}
        <Line className="values">
          {stats ? (
            <>
              Разброс по моделям: от <strong>{formatMoney(stats.min)}</strong> до{' '}
              <strong>{formatMoney(stats.max)}</strong> {unitText}.{' '}
            </>
          ) : (
            'Отметьте хотя бы одну модель — подсказка считается по отмеченным. '
          )}
          {typo(`Утверждённые ПРЦ на ${dateText}, база — ${basisText(full.basis)}, без НДС.`)}
          {yearsDiffer &&
            usedYear !== null &&
            ` ${typo(`У части моделей нет цен на ${usedYear} год — взят базовый год их ПРЦ.`)}`}
        </Line>
      </div>

      <Fieldset>
        <legend>Модели для объекта</legend>
        <ModelsHead>
          <p>
            {typo(
              picked === all.length
                ? 'Отмечены все модели группы. Снимите галочку с машин, которых на объекте нет, — подсказка пересчитается.'
                : `Подсказка по ${picked} из ${all.length} ${modelsWord(all.length)}.`,
            )}
          </p>
          {picked !== all.length && (
            <TextButton type="button" onClick={() => onChecked(null)}>
              Отметить все
            </TextButton>
          )}
        </ModelsHead>
        <Models>
          {full.models.map((m) => (
            <ModelRow key={m.model_id} data-off={isOn(m.model_id) ? undefined : 'true'}>
              <label>
                <input
                  type="checkbox"
                  checked={isOn(m.model_id)}
                  onChange={() => toggle(m.model_id)}
                />
                <span className="name">{m.model_name}</span>
                <span className="value">{formatMoney(m.value)}</span>
                <span className="meta">{modelMeta(m, usedYear)}</span>
              </label>
            </ModelRow>
          ))}
        </Models>
      </Fieldset>

      {full.missing_prc.length > 0 && <MissingList full={full} dateText={dateText} />}
    </Panel>
  );
}

function MissingList({ full, dateText }: { full: GroupRateSuggestion; dateText: string }) {
  return (
    <Missing>
      <p>{typo(`Без утверждённой ПРЦ на ${dateText} — в подсказку не входят:`)}</p>
      <ul>
        {full.missing_prc.map((m) => (
          <li key={m.model_id}>{m.model_name}</li>
        ))}
      </ul>
    </Missing>
  );
}
